import { useClerk } from "@clerk/clerk-react";
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '../../../shared/components/LanguageSwitcher';

export const NoUser = () => {
    const { t } = useTranslation();
    const { redirectToSignIn, redirectToSignUp } = useClerk();

    return (
        <section className="flex flex-col justify-center w-full grow items-center px-3 py-6 sm:px-6 sm:py-12 animate-fade-in">
            <div className="w-full max-w-4xl bg-card border-2 border-border/50 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-12 shadow-lg">
                {/* Title */}
                <h1 className="text-2xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-3 sm:mb-6 text-center tracking-wide">
                    {t('landing.title')}
                </h1>

                {/* Description */}
                <p className="text-sm sm:text-lg text-muted-foreground leading-relaxed text-center max-w-2xl mx-auto mb-6 sm:mb-10">
                    {t('landing.description')}
                </p>

                {/* Content Grid - Side by side on non-mobile */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-8 lg:gap-10 items-center">
                    {/* Left Column - Bullet Points */}
                    <div className="space-y-2 sm:space-y-4">
                        <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-5 sm:size-6 text-foreground flex-shrink-0">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z" />
                            </svg>
                            <span className="text-sm sm:text-base text-foreground font-medium">{t('landing.benefit1')}</span>
                        </div>

                        <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-5 sm:size-6 text-foreground flex-shrink-0">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                            </svg>
                            <span className="text-sm sm:text-base text-foreground font-medium">{t('landing.benefit2')}</span>
                        </div>

                        <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-5 sm:size-6 text-foreground flex-shrink-0">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z" />
                            </svg>
                            <span className="text-sm sm:text-base text-foreground font-medium">{t('landing.benefit3')}</span>
                        </div>
                    </div>

                    {/* Right Column - Logo */}
                    <div className="hidden lg:flex justify-center items-center animate-zoom-in">
                        <img
                            src="/logo.png"
                            alt="Gruntled logo"
                            className="w-full object-contain"
                        />
                    </div>
                </div>

                {/* CTA Button */}
                <div className="flex justify-center mt-6 sm:mt-10">
                    <button
                        onClick={() => redirectToSignUp()}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-base sm:px-12 sm:py-4 sm:text-xl font-semibold rounded-full transition-all duration-200 flex items-center justify-center gap-2 sm:gap-3"
                    >
                        <span>{t('landing.ctaButton')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4 sm:size-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m5.25 4.5 7.5 7.5-7.5 7.5m6-15 7.5 7.5-7.5 7.5" />
                        </svg>
                    </button>
                </div>

                {/* Sign up link */}
                <p className="text-center mt-4 sm:mt-6 text-sm sm:text-base text-muted-foreground">
                    {t('landing.signupPrompt')}{' '}
                    <button
                        onClick={() => redirectToSignUp()}
                        className="text-foreground hover:text-primary font-medium underline underline-offset-2 transition-colors"
                    >
                        {t('landing.signupLink')}
                    </button>
                </p>

                {/* Logo for mobile */}
                <div className="flex lg:hidden justify-center mt-6 sm:mt-8 animate-zoom-in">
                    <img
                        src="/logo.png"
                        alt="Gruntled logo"
                        className="w-full max-w-md object-contain"
                    />
                </div>

                {/* Badges */}
                <div className="flex justify-center items-center gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border/30">
                    <a target="_blank" rel="noopener noreferrer" href="https://betalist.com/startups/gruntled?utm_campaign=badge-gruntled&utm_medium=badge&utm_source=badge-featured">
                        <img alt="Gruntled - Featured on BetaList" width="156" height="54" style={{ width: 156, height: 54 }} src="https://betalist.com/badges/featured?id=152993&theme=gray" />
                    </a>
                </div>

                {/* Language Switcher */}
                <div className="flex justify-center mt-4 sm:mt-6">
                    <LanguageSwitcher />
                </div>
            </div>
        </section>
    )
}
